import React, { useState, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import CheckBox from "../shared/TailwindComponents/CheckBox";
import {
  PiXLight,
  PiFloppyDiskLight,
  PiFileTextLight,
  PiCalendarLight,
  PiFunnelLight,
  PiChartBarLight,
  PiChartPieSliceLight,
  PiChartLineUpLight,
  PiTableLight,
  PiTextTLight,
  PiListBulletsLight,
  PiPlusLight,
  PiTrashLight,
  PiArrowRightLight,
  PiBookmarkSimpleLight,
  PiDownloadSimpleLight,
  PiDotsSixVerticalLight,
  PiArrowUpLight,
  PiArrowDownLight,
  PiPencilSimpleLight,
} from "react-icons/pi";
import apis from "../../utils/apis";
import FieldShell, { fieldSurface } from "../shared/TailwindComponents/FieldShell";
import Textarea from "../shared/TailwindComponents/Textarea";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface ReportSection {
  id: string;
  type: "summary_cards" | "bar_chart" | "line_chart" | "pie_chart" | "table" | "text_block";
  label: string;
  enabled: boolean;
  content?: string;
}

interface SavedReportConfig {
  id: number;
  name: string;
  createdAt: string;
  updatedAt?: string;
  config: ReportConfig;
}

interface ReportConfig {
  title: string;
  description: string;
  timeRange: string;
  customDateFrom: string;
  customDateTo: string;
  categories: string[];
  sections: ReportSection[];
  includeComparison: boolean;
  comparisonPeriod: string;
  orientation: "portrait" | "landscape";
  includeHeader: boolean;
  includeFooter: boolean;
  groupBy: "category" | "date" | "none";
}

interface CreateReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onGenerate: (config: ReportConfig) => void;
  activeTab: string;
  currentTimeRange: string;
  currentFilters: string[];
  customDateFrom: string;
  customDateTo: string;
  filterOptions: { value: string; label: string }[];
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const SECTION_TYPES: { type: ReportSection["type"]; label: string; icon: React.ReactNode }[] = [
  { type: "summary_cards", label: "Summary Cards", icon: <PiListBulletsLight className="w-4 h-4" /> },
  { type: "bar_chart", label: "Bar Chart", icon: <PiChartBarLight className="w-4 h-4" /> },
  { type: "line_chart", label: "Line Chart", icon: <PiChartLineUpLight className="w-4 h-4" /> },
  { type: "pie_chart", label: "Pie Chart", icon: <PiChartPieSliceLight className="w-4 h-4" /> },
  { type: "table", label: "Data Table", icon: <PiTableLight className="w-4 h-4" /> },
  { type: "text_block", label: "Text / Notes", icon: <PiTextTLight className="w-4 h-4" /> },
];

const COMPARISON_OPTIONS = [
  { value: "previous", label: "Previous period" },
  { value: "last-year", label: "Same period last year" },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const CreateReportModal: React.FC<CreateReportModalProps> = ({
  isOpen,
  onClose,
  onGenerate,
  activeTab,
  currentTimeRange,
  currentFilters,
  customDateFrom: propDateFrom,
  customDateTo: propDateTo,
  filterOptions,
}) => {
  const { t } = useTranslation();

  const defaultSections: ReportSection[] = [
    { id: "sec-1", type: "summary_cards", label: "Key Metrics", enabled: true },
    { id: "sec-2", type: "bar_chart", label: "Activity Trends", enabled: true },
    { id: "sec-3", type: "line_chart", label: "Growth Over Time", enabled: true },
    { id: "sec-4", type: "pie_chart", label: "Distribution", enabled: true },
    { id: "sec-5", type: "table", label: "Detailed Breakdown", enabled: false },
  ];

  const [config, setConfig] = useState<ReportConfig>({
    title: "Analytics Report",
    description: "",
    timeRange: currentTimeRange,
    customDateFrom: propDateFrom,
    customDateTo: propDateTo,
    categories: currentFilters.length > 0 ? [...currentFilters] : filterOptions.map((o) => o.value),
    sections: defaultSections,
    includeComparison: false,
    comparisonPeriod: "previous",
    orientation: "portrait",
    includeHeader: true,
    includeFooter: true,
    groupBy: "category",
  });

  const [savedConfigs, setSavedConfigs] = useState<SavedReportConfig[]>([]);
  const [activePanel, setActivePanel] = useState<"content" | "layout" | "saved">("content");
  const [saveName, setSaveName] = useState("");
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [loadingSaved, setLoadingSaved] = useState(false);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);

  // Drag state
  const dragItem = useRef<number | null>(null);
  const dragOverItem = useRef<number | null>(null);

  /* ---- API helpers ---- */

  const fetchSavedConfigs = useCallback(async () => {
    setLoadingSaved(true);
    try {
      const res = await apis.get("/analytics/report-configs");
      if (res.status === 200 && Array.isArray(res.data)) {
        setSavedConfigs(res.data);
      }
    } catch {
      // silent
    } finally {
      setLoadingSaved(false);
    }
  }, []);

  // Load saved configs from API when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchSavedConfigs();
      setConfig((prev) => ({
        ...prev,
        timeRange: currentTimeRange,
        customDateFrom: propDateFrom,
        customDateTo: propDateTo,
        categories: currentFilters.length > 0 ? [...currentFilters] : filterOptions.map((o) => o.value),
      }));
    }
  }, [isOpen]);

  if (!isOpen) return null;

  /* ---- Config helpers ---- */

  const updateConfig = <K extends keyof ReportConfig>(key: K, value: ReportConfig[K]) => {
    setConfig((prev) => ({ ...prev, [key]: value }));
  };

  const toggleCategory = (val: string) => {
    setConfig((prev) => ({
      ...prev,
      categories: prev.categories.includes(val)
        ? prev.categories.filter((c) => c !== val)
        : [...prev.categories, val],
    }));
  };

  const toggleSection = (id: string) => {
    setConfig((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s)),
    }));
  };

  const removeSection = (id: string) => {
    setConfig((prev) => ({ ...prev, sections: prev.sections.filter((s) => s.id !== id) }));
  };

  const addSection = (type: ReportSection["type"], label: string) => {
    const newId = `sec-${Date.now()}`;
    const newSection: ReportSection = { id: newId, type, label, enabled: true };
    if (type === "text_block") newSection.content = "";
    setConfig((prev) => ({
      ...prev,
      sections: [...prev.sections, newSection],
    }));
  };

  const updateSectionContent = (id: string, content: string) => {
    setConfig((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.id === id ? { ...s, content } : s)),
    }));
  };

  const updateSectionLabel = (id: string, label: string) => {
    setConfig((prev) => ({
      ...prev,
      sections: prev.sections.map((s) => (s.id === id ? { ...s, label } : s)),
    }));
  };

  /* ---- Reorder ---- */

  const moveSection = (fromIndex: number, toIndex: number) => {
    if (toIndex < 0 || toIndex >= config.sections.length) return;
    setConfig((prev) => {
      const updated = [...prev.sections];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return { ...prev, sections: updated };
    });
  };

  const handleDragStart = (index: number) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = () => {
    if (dragItem.current !== null && dragOverItem.current !== null && dragItem.current !== dragOverItem.current) {
      moveSection(dragItem.current, dragOverItem.current);
    }
    dragItem.current = null;
    dragOverItem.current = null;
  };

  /* ---- Save / Load / Delete (API) ---- */

  const handleSaveConfig = async () => {
    if (!saveName.trim()) return;
    try {
      const res = await apis.post("/analytics/report-configs", {
        name: saveName.trim(),
        config: { ...config },
      });
      if (res.status === 201 && res.data) {
        setSavedConfigs((prev) => [res.data, ...prev]);
        setSaveName("");
        setShowSaveInput(false);
      }
    } catch {
      // silent
    }
  };

  const handleLoadConfig = (saved: SavedReportConfig) => {
    setConfig({ ...saved.config });
    setActivePanel("content");
  };

  const handleDeleteConfig = async (id: number) => {
    try {
      await apis.delete(`/analytics/report-configs/${id}`);
      setSavedConfigs((prev) => prev.filter((c) => c.id !== id));
    } catch {
      // silent
    }
  };

  /* ---- Rendering helpers ---- */

  const timeRangeOptions = [
    { value: "last-7-days", label: "Last 7 days" },
    { value: "last-30-days", label: "Last 30 days" },
    { value: "last-90-days", label: "Last 90 days" },
    { value: "last-year", label: "Last year" },
    { value: "custom", label: "Custom range" },
  ];

  const sectionIcon = (type: ReportSection["type"]) =>
    SECTION_TYPES.find((s) => s.type === type)?.icon ?? <PiFileTextLight className="w-4 h-4" />;

  /* ================================================================ */
  /*  JSX                                                             */
  /* ================================================================ */

  return (
    <div className="fixed inset-0 z-[9999999] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-ink/20 backdrop-blur-[2px]" onClick={onClose} />

      {/* Modal */}
      <div className="relative w-full max-w-5xl max-h-[90vh] bg-white dark:bg-gray-900 rounded-lg shadow-2xl flex flex-col overflow-hidden animate-fade-in">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{t("Create Report")}</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {t("Configure the content and layout of your report")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <PiXLight className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Tab Nav */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
          {(["content", "layout", "saved"] as const).map((panel) => (
            <button
              key={panel}
              onClick={() => setActivePanel(panel)}
              className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activePanel === panel
                  ? "border-gray-900 text-gray-900 dark:border-white dark:text-white"
                  : "border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
              }`}
            >
              {panel === "content" && t("Content")}
              {panel === "layout" && t("Layout")}
              {panel === "saved" && `${t("Saved")} (${savedConfigs.length})`}
            </button>
          ))}
        </div>

        {/* Body — #F5F5F5 background */}
        <div className="flex-1 overflow-y-auto px-6 py-5" style={{ backgroundColor: "#F5F5F5" }}>
          {/* CONTENT PANEL */}
          {activePanel === "content" && (
            <div className="space-y-6">
              {/* Title & Description */}
              <div className="grid grid-cols-2 gap-4">
                <FieldShell label={t("Report Title")} translate={false}>
                  <input
                    type="text"
                    value={config.title}
                    onChange={(e) => updateConfig("title", e.target.value)}
                    className={`${fieldSurface()} px-3 py-2`}
                    placeholder={t("e.g. Monthly Analytics Report")}
                  />
                </FieldShell>
                <FieldShell
                  label={t("Description")}
                  description={t("Optional")}
                  translate={false}
                >
                  <input
                    type="text"
                    value={config.description}
                    onChange={(e) => updateConfig("description", e.target.value)}
                    className={`${fieldSurface()} px-3 py-2`}
                    placeholder={t("Brief description for the report cover page")}
                  />
                </FieldShell>
              </div>

              {/* Time Range */}
              <FieldShell label={t("Time Range")} translate={false}>
                <div className="flex flex-wrap gap-2">
                  {timeRangeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => updateConfig("timeRange", opt.value)}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors cursor-pointer ${
                        config.timeRange === opt.value
                          ? "bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-400 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
                      }`}
                    >
                      <PiCalendarLight className="w-3 h-3 inline mr-1" />
                      {t(opt.label)}
                    </button>
                  ))}
                </div>
                {config.timeRange === "custom" && (
                  <div className="flex items-center gap-2 mt-3">
                    <input
                      type="date"
                      value={config.customDateFrom}
                      onChange={(e) => updateConfig("customDateFrom", e.target.value)}
                      className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                    <span className="text-xs text-gray-400">to</span>
                    <input
                      type="date"
                      value={config.customDateTo}
                      onChange={(e) => updateConfig("customDateTo", e.target.value)}
                      className="px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-400"
                    />
                  </div>
                )}
              </FieldShell>

              {/* Comparison */}
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <CheckBox
                    label={t("Include period comparison")}
                    translate={false}
                    checked={config.includeComparison}
                    onChange={(e) => updateConfig("includeComparison", e.target.checked)}
                  />
                </div>
                {config.includeComparison && (
                  <div className="flex gap-2">
                    {COMPARISON_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => updateConfig("comparisonPeriod", opt.value)}
                        className={`px-3 py-1.5 text-xs rounded-md border transition-colors cursor-pointer ${
                          config.comparisonPeriod === opt.value
                            ? "bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white"
                            : "bg-white text-gray-600 border-gray-300 hover:border-gray-400 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
                        }`}
                      >
                        {t(opt.label)}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Categories */}
              <FieldShell label={t("Categories to Include")} translate={false}>
                <div className="flex flex-wrap gap-2">
                  {filterOptions.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => toggleCategory(opt.value)}
                      className={`px-3 py-1.5 text-xs rounded-md border transition-colors cursor-pointer ${
                        config.categories.includes(opt.value)
                          ? "bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-400 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
                      }`}
                    >
                      <PiFunnelLight className="w-3 h-3 inline mr-1" />
                      {t(opt.label)}
                    </button>
                  ))}
                </div>
              </FieldShell>

              {/* Report Sections — drag-to-reorder */}
              <FieldShell label={t("Report Sections")} translate={false}>
                <div className="space-y-2">
                  {config.sections.map((sec, idx) => (
                    <div
                      key={sec.id}
                      draggable
                      onDragStart={() => handleDragStart(idx)}
                      onDragEnter={() => handleDragEnter(idx)}
                      onDragEnd={handleDragEnd}
                      onDragOver={(e) => e.preventDefault()}
                      className={`rounded-md border transition-colors ${
                        sec.enabled
                          ? "bg-white border-gray-200 dark:bg-gray-800 dark:border-gray-700"
                          : "bg-gray-50 border-gray-100 opacity-50 dark:bg-gray-800/50 dark:border-gray-700/50"
                      }`}
                    >
                      <div className="flex items-center justify-between px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          {/* Drag handle */}
                          <span className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600">
                            <PiDotsSixVerticalLight className="w-4 h-4" />
                          </span>
                          <CheckBox
                            label={sec.label}
                            hideLabel
                            translate={false}
                            checked={sec.enabled}
                            onChange={() => toggleSection(sec.id)}
                          />
                          <span className="text-gray-500">{sectionIcon(sec.type)}</span>
                          {/* Editable label */}
                          {editingLabelId === sec.id ? (
                            <input
                              type="text"
                              value={sec.label}
                              onChange={(e) => updateSectionLabel(sec.id, e.target.value)}
                              onBlur={() => setEditingLabelId(null)}
                              onKeyDown={(e) => e.key === "Enter" && setEditingLabelId(null)}
                              className="text-sm text-gray-800 dark:text-gray-200 bg-transparent border-b border-gray-400 focus:outline-none px-0 py-0"
                              autoFocus
                            />
                          ) : (
                            <span
                              className="text-sm text-gray-800 dark:text-gray-200 cursor-pointer hover:underline"
                              onClick={() => setEditingLabelId(sec.id)}
                              title={t("Click to rename")}
                            >
                              {t(sec.label)}
                            </span>
                          )}
                          <span className="text-[10px] text-gray-400 uppercase tracking-wide">{sec.type.replace(/_/g, " ")}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          {/* Move up / down */}
                          <button
                            onClick={() => moveSection(idx, idx - 1)}
                            disabled={idx === 0}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors cursor-pointer"
                            title={t("Move up")}
                          >
                            <PiArrowUpLight className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                          <button
                            onClick={() => moveSection(idx, idx + 1)}
                            disabled={idx === config.sections.length - 1}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-30 transition-colors cursor-pointer"
                            title={t("Move down")}
                          >
                            <PiArrowDownLight className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                          <button
                            onClick={() => setEditingLabelId(sec.id)}
                            className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                            title={t("Rename")}
                          >
                            <PiPencilSimpleLight className="w-3.5 h-3.5 text-gray-400" />
                          </button>
                          <button
                            onClick={() => removeSection(sec.id)}
                            className="p-1 rounded hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors cursor-pointer"
                          >
                            <PiTrashLight className="w-3.5 h-3.5 text-gray-400 hover:text-danger-500" />
                          </button>
                        </div>
                      </div>

                      {/* Text block editor */}
                      {sec.type === "text_block" && sec.enabled && (
                        <div className="px-3 pb-3 pt-0">
                          <Textarea
                            id={`report-section-${sec.id}`}
                            label=""
                            value={sec.content || ""}
                            onChange={(e) => updateSectionContent(sec.id, e.target.value)}
                            placeholder={t("Write your notes, commentary, or analysis here...")}
                            className="resize-y"
                            rows={3}
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Add section */}
                <div className="mt-3">
                  <p className="text-[11px] text-gray-400 mb-1.5">{t("Add a section")}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {SECTION_TYPES.map((st) => (
                      <button
                        key={st.type}
                        onClick={() => addSection(st.type, st.label)}
                        className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-500 border border-dashed border-gray-300 dark:border-gray-600 rounded-md hover:border-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors cursor-pointer"
                      >
                        <PiPlusLight className="w-3 h-3" />
                        {t(st.label)}
                      </button>
                    ))}
                  </div>
                </div>
              </FieldShell>
            </div>
          )}

          {/* LAYOUT PANEL */}
          {activePanel === "layout" && (
            <div className="space-y-6">
              {/* Orientation */}
              <FieldShell label={t("Page Orientation")} translate={false}>
                <div className="flex gap-2">
                  {(["portrait", "landscape"] as const).map((o) => (
                    <button
                      key={o}
                      onClick={() => updateConfig("orientation", o)}
                      className={`px-4 py-2 text-xs rounded-md border transition-colors cursor-pointer ${
                        config.orientation === o
                          ? "bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-400 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
                      }`}
                    >
                      {t(o.charAt(0).toUpperCase() + o.slice(1))}
                    </button>
                  ))}
                </div>
              </FieldShell>

              {/* Group By */}
              <FieldShell label={t("Group Charts By")} translate={false}>
                <div className="flex gap-2">
                  {([
                    { value: "category", label: "Category" },
                    { value: "date", label: "Date Range" },
                    { value: "none", label: "No Grouping" },
                  ] as const).map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => updateConfig("groupBy", opt.value)}
                      className={`px-4 py-2 text-xs rounded-md border transition-colors cursor-pointer ${
                        config.groupBy === opt.value
                          ? "bg-gray-900 text-white border-gray-900 dark:bg-white dark:text-gray-900 dark:border-white"
                          : "bg-white text-gray-600 border-gray-300 hover:border-gray-400 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600"
                      }`}
                    >
                      {t(opt.label)}
                    </button>
                  ))}
                </div>
              </FieldShell>

              {/* Header / Footer */}
              <div className="space-y-3">
                <CheckBox
                  label={t("Include report header with title and date")}
                  translate={false}
                  checked={config.includeHeader}
                  onChange={(e) => updateConfig("includeHeader", e.target.checked)}
                />
                <CheckBox
                  label={t("Include page numbers and footer")}
                  translate={false}
                  checked={config.includeFooter}
                  onChange={(e) => updateConfig("includeFooter", e.target.checked)}
                />
              </div>

              {/* Preview */}
              <FieldShell label={t("Preview")} translate={false}>
                <div
                  className={`border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800/50 p-4 ${
                    config.orientation === "landscape" ? "aspect-[1.41/1] max-w-md" : "aspect-[1/1.41] max-w-[240px]"
                  }`}
                >
                  {config.includeHeader && (
                    <div className="border-b border-gray-300 dark:border-gray-600 pb-2 mb-3">
                      <div className="h-2.5 w-32 bg-gray-300 dark:bg-gray-600 rounded" />
                      <div className="h-1.5 w-20 bg-gray-200 dark:bg-gray-700 rounded mt-1.5" />
                    </div>
                  )}
                  <div className="space-y-2">
                    {config.sections
                      .filter((s) => s.enabled)
                      .map((sec) => (
                        <div key={sec.id} className="flex items-center gap-2">
                          <span className="text-gray-400">{sectionIcon(sec.type)}</span>
                          <div className="h-1.5 flex-1 bg-gray-200 dark:bg-gray-700 rounded" />
                        </div>
                      ))}
                  </div>
                  {config.includeFooter && (
                    <div className="border-t border-gray-300 dark:border-gray-600 pt-1.5 mt-3">
                      <div className="h-1 w-12 bg-gray-200 dark:bg-gray-700 rounded mx-auto" />
                    </div>
                  )}
                </div>
              </FieldShell>
            </div>
          )}

          {/* SAVED PANEL */}
          {activePanel === "saved" && (
            <div className="space-y-4">
              {loadingSaved ? (
                <div className="text-center py-12">
                  <p className="text-sm text-gray-400">{t("Loading saved configurations...")}</p>
                </div>
              ) : savedConfigs.length === 0 ? (
                <div className="text-center py-12">
                  <PiBookmarkSimpleLight className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                  <p className="text-sm text-gray-500 dark:text-gray-400">{t("No saved report configurations yet")}</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {t("Configure a report and save it for quick reuse")}
                  </p>
                </div>
              ) : (
                savedConfigs.map((saved) => (
                  <div
                    key={saved.id}
                    className="flex items-center justify-between px-4 py-3 border border-gray-200 dark:border-gray-700 rounded-md bg-white hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                  >
                    <div>
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{saved.name}</p>
                      <p className="text-[11px] text-gray-400 mt-0.5">
                        {t("Created")} {new Date(saved.createdAt).toLocaleDateString()} &middot;{" "}
                        {saved.config.sections?.filter((s: any) => s.enabled).length ?? 0} {t("sections")} &middot;{" "}
                        {saved.config.categories?.length ?? 0} {t("categories")}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleLoadConfig(saved)}
                        className="px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                      >
                        {t("Load")}
                      </button>
                      <button
                        onClick={() => handleDeleteConfig(saved.id)}
                        className="p-1.5 rounded-md hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors cursor-pointer"
                      >
                        <PiTrashLight className="w-4 h-4 text-gray-400 hover:text-danger-500" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-2">
            {showSaveInput ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={saveName}
                  onChange={(e) => setSaveName(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSaveConfig()}
                  placeholder={t("Config name...")}
                  className="px-3 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-1 focus:ring-gray-400 w-44"
                  autoFocus
                />
                <button
                  onClick={handleSaveConfig}
                  disabled={!saveName.trim()}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-md hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 transition-colors cursor-pointer"
                >
                  {t("Save")}
                </button>
                <button
                  onClick={() => { setShowSaveInput(false); setSaveName(""); }}
                  className="px-2 py-1.5 text-xs text-gray-500 hover:text-gray-700 transition-colors cursor-pointer"
                >
                  {t("Cancel")}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowSaveInput(true)}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 border border-gray-300 rounded-md hover:bg-gray-100 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700 transition-colors cursor-pointer"
              >
                <PiFloppyDiskLight className="w-4 h-4" />
                {t("Save Configuration")}
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 transition-colors cursor-pointer"
            >
              {t("Cancel")}
            </button>
            <button
              onClick={() => onGenerate(config)}
              disabled={config.categories.length === 0 || config.sections.filter((s) => s.enabled).length === 0}
              className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-gray-900 dark:bg-white dark:text-gray-900 rounded-md hover:bg-gray-800 dark:hover:bg-gray-100 disabled:opacity-40 transition-colors cursor-pointer"
            >
              <PiDownloadSimpleLight className="w-4 h-4" />
              {t("Generate Report")}
              <PiArrowRightLight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateReportModal;
